import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from 'react';
import apiService, { TOKEN_CHANGE_EVENT, CRM_API_KEY } from '../services/api';
import { API_ENDPOINTS } from '../utils/apiUrls';

const AuthContext = createContext(null);

const hasTokenInStorage = () => {
  if (typeof window === 'undefined') return false;
  try {
    return Boolean(localStorage.getItem('access_token'));
  } catch {
    return false;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(false);
  const [hasAccessToken, setHasAccessToken] = useState(() => hasTokenInStorage());

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const updateTokenState = () => setHasAccessToken(hasTokenInStorage());
    updateTokenState();

    window.addEventListener(TOKEN_CHANGE_EVENT, updateTokenState);
    window.addEventListener('storage', updateTokenState);

    return () => {
      window.removeEventListener(TOKEN_CHANGE_EVENT, updateTokenState);
      window.removeEventListener('storage', updateTokenState);
    };
  }, []);

  const fetchMe = useCallback(async () => {
    const me = await apiService.get(API_ENDPOINTS.AUTH.ME);
    setUser(me);
    try {
      if (me?.tier != null) localStorage.setItem('user_tier', me.tier);
      if (me?.admin_email) localStorage.setItem('admin_email', me.admin_email);
    } catch {
      // ignore storage errors
    }
    return me;
  }, []);

  const loginByEmail = useCallback(async (email) => {
    if (!email) return user;
    // Skip re-auth if we already have the same user and token
    if (user?.email === email && localStorage.getItem('access_token')) return user;

    setLoadingAuth(true);
    try {
      const login = await apiService.post(
        API_ENDPOINTS.AUTH.LOGIN,
        { email },
        { headers: { 'X-CRM-API-Key': CRM_API_KEY } }
      );
      apiService.saveTokens({
        access_token: login?.access_token,
        refresh_token: login?.refresh_token,
      });
      if (login?.access_token) setHasAccessToken(true);
      return await fetchMe();
    } finally {
      setLoadingAuth(false);
    }
  }, [fetchMe, user]);


  const value = useMemo(
    () => ({ user, loadingAuth, loginByEmail, fetchMe, hasAccessToken }),
    [user, loadingAuth, loginByEmail, fetchMe, hasAccessToken]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
